import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { ACCEPT_LANGUAGE } from '../tokens/accept-language.token';

export type AppLang = 'pt-BR' | 'en';

const STORAGE_KEY = 'lang';
const SUPPORTED: AppLang[] = ['pt-BR', 'en'];
const DEFAULT_LANG: AppLang = 'pt-BR';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly isBrowser: boolean;

  constructor(
    private readonly transloco: TranslocoService,
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(ACCEPT_LANGUAGE) private readonly acceptLanguage: string,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Idiomas suportados (para o seletor). */
  get available(): AppLang[] {
    return [...SUPPORTED];
  }

  get current(): AppLang {
    return this.normalize(this.transloco.getActiveLang());
  }

  /**
   * Define o idioma inicial e carrega as traduções. Chamado no APP_INITIALIZER.
   */
  async init(): Promise<void> {
    const lang = this.resolveInitialLang();
    this.transloco.setActiveLang(lang);
    await this.transloco.load(lang).toPromise();
    this.applyDocumentLang(lang);
  }

  /** Troca de idioma em runtime e persiste a escolha do usuário. */
  setLang(lang: AppLang): void {
    const normalized = this.normalize(lang);
    this.transloco.setActiveLang(normalized);
    if (this.isBrowser) {
      try {
        localStorage.setItem(STORAGE_KEY, normalized);
      } catch {
        /* localStorage indisponível — ignora */
      }
    }
    this.applyDocumentLang(normalized);
  }

  private resolveInitialLang(): AppLang {
    if (this.isBrowser) {
      const stored = this.safeGetStored();
      if (stored) return stored;
      return this.normalize(navigator.language);
    }
    // SSR: usa o Accept-Language enviado pelo browser
    return this.normalize(this.acceptLanguage);
  }

  private safeGetStored(): AppLang | null {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value && SUPPORTED.includes(value as AppLang) ? (value as AppLang) : null;
    } catch {
      return null;
    }
  }

  private normalize(code: string | null | undefined): AppLang {
    const lower = (code ?? '').toLowerCase();
    if (lower.startsWith('pt')) return 'pt-BR';
    if (lower.startsWith('en')) return 'en';
    return DEFAULT_LANG;
  }

  private applyDocumentLang(lang: AppLang): void {
    if (this.isBrowser && typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }
}
